import { useMemo, useId } from 'react';

interface OctagonAvatarProps {
  n?: number;
  r?: number;
  angle?: number;
  size?: number;
  imageSource?: string;
  borderColor?: string;
  borderWidth?: number;
  fallbackImage?: string;
  smoothness?: number;
}

const OctagonAvatar = ({ 
  n = 8, // Changed to 8 for octagon
  r: _r = 0.25, 
  angle = -22.5, // Rotated to have one corner at the top
  size = 300, 
  imageSource, 
  borderColor = 'white', 
  borderWidth = 4,
  fallbackImage = '/noAvatar.png',
  smoothness = 0.15 // Control corner smoothness (0-0.3 range works best)
}: OctagonAvatarProps) => {
  const pathData = useMemo(() => {
    const angleRad = (angle * Math.PI) / 180;
    const center = size / 2;
    
    // Calculate the main radius
    const mainRadius = center * 0.95; // Slightly smaller than full size for padding
    
    let path = '';
    const points = [];
    
    // First, calculate all the main vertices
    for (let i = 0; i < n; i++) {
      const baseAngle = (i * 2 * Math.PI) / n + angleRad;
      const x = center + mainRadius * Math.cos(baseAngle);
      const y = center + mainRadius * Math.sin(baseAngle);
      points.push({ x, y, angle: baseAngle });
    }
    
    // Now create the path with smooth corners
    for (let i = 0; i < n; i++) {
      const current = points[i];
      const next = points[(i + 1) % n];
      const prev = points[(i - 1 + n) % n];
      
      // Calculate where the curve starts and ends on each edge
      // This creates the smooth transition at corners
      const startRatio = 1 - smoothness;
      const endRatio = smoothness;
      
      // Start point of the curve (end of the straight line from previous corner)
      const startX = prev.x + (current.x - prev.x) * startRatio;
      const startY = prev.y + (current.y - prev.y) * startRatio;
      
      // End point of the curve (start of the straight line to next corner)
      const endX = current.x + (next.x - current.x) * endRatio;
      const endY = current.y + (next.y - current.y) * endRatio;
      
      // Control points for the bezier curve
      // These create the smooth rounded corner
      const cp1x = startX + (current.x - startX) * 0.5;
      const cp1y = startY + (current.y - startY) * 0.5;
      const cp2x = endX + (current.x - endX) * 0.5;
      const cp2y = endY + (current.y - endY) * 0.5;
      
      if (i === 0) {
        // Move to the start point of the first curve
        const firstStartX = points[n-1].x + (points[0].x - points[n-1].x) * startRatio;
        const firstStartY = points[n-1].y + (points[0].y - points[n-1].y) * startRatio;
        path += `M ${firstStartX.toFixed(3)} ${firstStartY.toFixed(3)} `;
      }
      
      // Create the cubic bezier curve for the corner
      path += `C ${cp1x.toFixed(3)} ${cp1y.toFixed(3)}, ${cp2x.toFixed(3)} ${cp2y.toFixed(3)}, ${endX.toFixed(3)} ${endY.toFixed(3)} `;
      
      // Add the straight line to the next corner's curve start
      if (i < n - 1) {
        const nextStartX = current.x + (next.x - current.x) * startRatio;
        const nextStartY = current.y + (next.y - current.y) * startRatio;
        path += `L ${nextStartX.toFixed(3)} ${nextStartY.toFixed(3)} `;
      }
    }
    
    return path + 'Z'; // Close the path
  }, [n, angle, size, smoothness]);
  
  // Generate a unique ID for each instance to avoid conflicts
  // Use React's useId() which is SSR-safe and generates consistent IDs
  const id = useId();
  const clipPathId = useMemo(() => `octagonClip-${id.replace(/:/g, '-')}`, [id]);
  
  return (
    <div style={{ width: size, height: size, position: 'relative' }}>
      <svg 
        height="100%" 
        width="100%" 
        viewBox={`0 0 ${size} ${size}`}
        style={{ position: 'absolute', top: 0, left: 0 }}
      >
        {/* Define the clip path */}
        <defs>
          <clipPath id={clipPathId}>
            <path d={pathData} />
          </clipPath>
        </defs>
        
        {/* Background octagon for border */}
        <path 
          d={pathData} 
          fill="none" 
          stroke={borderColor} 
          strokeWidth={borderWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        
        {/* Image with octagon clip path */}
        <image
          href={imageSource || fallbackImage}
          width={size}
          height={size}
          clipPath={`url(#${clipPathId})`}
          preserveAspectRatio="xMidYMid slice"
        />
      </svg>
    </div>
  );
};

export default OctagonAvatar;